import React from 'react';
import { useState, useEffect, forwardRef, useRef, useMemo } from 'react';
import { getStore, usePlayerStore, useLayoutStore } from '../../../stores/StoreContext';
import CONSTANTS from '../../../config/constants';

/**
 * Playhead Component
 * 
 * A draggable playhead indicator for video playback that shows the current playback position
 * and allows users to scrub through the video timeline.
 * 
 * @component
 * 
 * **Performance Optimizations:**
 * - requestAnimationFrame batching for smooth 60fps dragging
 * - Document-level mouse events for continuous drag tracking
 * - Memoized styles to prevent unnecessary object recreation
 * - Ref-based final position tracking to avoid stale state issues
 * - Sub-second precision during drag (no rounding) for smooth scrubbing
 * 
 * **Features:**
 * - Draggable playhead handle (white circle)
 * - Vertical line indicator when markers are displayed
 * - Click-to-seek on the playhead itself
 * - Auto-hides when video is in live mode
 * - Pauses video during drag, resumes on release
 * 
 * **Drag Behavior:**
 * 1. MouseDown: Pauses video, captures bounds, initializes drag state
 * 2. MouseMove: Calculates position, batches updates via RAF at 60fps
 * 3. MouseUp: Seeks video to final position, resumes playback
 * 
 * @param {Object} props - Component props
 * @param {React.Ref} ref - Forwarded ref to the playhead container element
 * 
 * @example
 * <Playhead ref={playheadRef} />
 */
const Playhead = forwardRef((props, ref) => {
  const PlayerStore = getStore('playerStore');

  {
    /* Listen to any changes in the stores... */
  }
  const duration = usePlayerStore((state) => state.duration);
  const currentTime = usePlayerStore((state) => state.currentTime);
  const isLive = usePlayerStore((state) => state.isLive);
  const dragging = usePlayerStore((state) => state.dragging);
  const displayMarkers = usePlayerStore((state) => state.displayMarkers);
  const scrubTime = usePlayerStore((state) => state.scrubTime);
  const mediaControlsElement = useLayoutStore((state) => state.containers.controls.element);

  // Component state
  const [refBounds, setRefBounds] = useState(null); // Bounding box for drag calculations
  const [controlsHeight, setControlsHeight] = useState(0); // Height of controls for vertical line

  // Performance refs
  const rafRef = useRef(null); // requestAnimationFrame ID for batched updates
  const pendingUpdateRef = useRef(null); // Pending time update for RAF callback
  const finalTimeRef = useRef(null); // Tracks final drag position (avoids stale state)

  // Track the height of the media controls element
  useEffect(() => {
    if (!mediaControlsElement) return;

    const updateHeight = () => {
      setControlsHeight(mediaControlsElement.offsetHeight - 10);
    };

    // Initial height
    updateHeight();

    // Observe size changes
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(mediaControlsElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mediaControlsElement]);

  /**
   * Handles mouse down on playhead - initiates drag operation
   * 
   * @param {MouseEvent} e - Mouse event
   * 
   * Actions:
   * - Prevents default browser behavior (text selection)
   * - Pauses video playback
   * - Enables drag mode
   * - Captures element bounds for position calculations
   * - Initializes final time ref with current position
   */
  const handleMouseDown = (e) => {
    e.preventDefault();
    PlayerStore.setPlaying(false);
    PlayerStore.setDragging(true);
    PlayerStore.setScrubTime(currentTime);
    finalTimeRef.current = currentTime;
    setRefBounds(ref.current.getBoundingClientRect());
  };

  /**
   * Handles mouse up - completes drag operation and seeks video
   * 
   * Actions:
   * - Gets final drag position from ref (not store - avoids stale state)
   * - Disables drag mode
   * - Seeks video to final position
   * - Resumes video playback
   * - Cancels any pending RAF updates
   * - Cleans up refs
   * 
   * **Important:** Uses finalTimeRef instead of scrubTime from store to avoid
   * stale state issues caused by React batching and RAF timing.
   */
  const handleMouseUp = () => {
    // Use the ref to get the actual final position (not stale store value)
    const finalTime = finalTimeRef.current !== null ? finalTimeRef.current : currentTime;

    PlayerStore.setDragging(false);
    PlayerStore.seekVideoTo(finalTime);
    PlayerStore.setPlaying(true);

    // Cancel any pending RAF updates
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pendingUpdateRef.current = null;
    finalTimeRef.current = null;
  };

  /**
   * Handles mouse move during drag - updates playhead position
   * 
   * @param {MouseEvent} e - Mouse event
   * 
   * **Performance Optimization:**
   * Uses requestAnimationFrame to batch updates at 60fps instead of processing
   * every mousemove event (which can fire 100+ times/second).
   * 
   * **Process:**
   * 1. Calculate new time based on mouse X position relative to bounds
   * 2. Clamp position to valid range (0 to width)
   * 3. Store pending update and final position in refs
   * 4. If no RAF pending, schedule update for next frame
   * 5. RAF callback applies the most recent position to store
   * 
   * **Note:** Does NOT round time during drag for smooth sub-second scrubbing.
   * This provides silky smooth playhead movement instead of jumpy 1-second increments.
   */
  const handleMouseMove = (e) => {
    if (!dragging || !refBounds) return;

    // Calculate new time based on mouse position
    let relativeX = e.clientX - refBounds.left;
    relativeX = Math.max(0, Math.min(relativeX, refBounds.width));
    let newTime = (relativeX / refBounds.width) * duration;

    // Store pending update (no rounding for smooth scrubbing)
    pendingUpdateRef.current = newTime;
    finalTimeRef.current = newTime; // Track for mouseUp

    // Batch updates at 60fps using RAF
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        const timeToSet = pendingUpdateRef.current;
        if (timeToSet !== null) {
          if (timeToSet >= duration) {
            // At end of video - could switch to live mode
            //PlayerStore.setActiveVideoSource(CONSTANTS.VIDEO_SOURCES.LIVE)
          } else {
            PlayerStore.setScrubTime(timeToSet);
            PlayerStore.setCurrentTime(timeToSet);
            PlayerStore.onSeek(timeToSet);
          }
        }
        rafRef.current = null;
      });
    }
  };

  /**
   * Handles click on playhead - seeks to clicked position
   * 
   * @param {MouseEvent} e - Mouse event
   * 
   * Allows users to click directly on the playhead to jump to a specific time
   * without dragging. Switches to recorded video source and seeks immediately.
   */
  const handleClickNav = (e) => {
    const rect = ref.current.getBoundingClientRect();
    let relativeX = e.clientX - rect.left;
    relativeX = Math.max(0, Math.min(relativeX, rect.width));
    const newTime = (relativeX / rect.width) * duration;

    PlayerStore.setActiveVideoSource(CONSTANTS.VIDEO_SOURCES.RECORDED);
    PlayerStore.setCurrentTime(newTime);
    PlayerStore.onSeek(newTime);
  };

  /**
   * Memoized style for playhead handle (white circle)
   * 
   * Only recreates when isLive changes, preventing unnecessary re-renders.
   * Position is calculated separately and applied dynamically.
   */
  const handleStyle = useMemo(() => ({
    position: 'absolute',
    top: '12px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: '#fff',
    border: '1px solid #333',
    cursor: 'pointer',
    zIndex: 9000,
    transform: 'translateX(-50%)',
    display: isLive ? 'none' : 'block',
  }), [isLive]);

  /**
   * Memoized style for vertical line indicator
   * 
   * Only recreates when displayMarkers, controlsHeight, or isLive changes.
   * Line is only visible when markers are displayed.
   */
  const handleLineStyle = useMemo(() => ({
    position: 'absolute',
    top: '12px',
    width: displayMarkers.length ? '1px' : '0px',
    height: `${controlsHeight}px`,
    background: '#000',
    cursor: 'pointer',
    zIndex: 8999,
    display: isLive ? 'none' : 'block',
  }), [displayMarkers.length, controlsHeight, isLive]);

  /**
   * Calculate playhead position as percentage
   * 
   * Uses scrubTime during drag for immediate visual feedback,
   * currentTime during normal playback.
   */
  const handlePosition = (dragging ? scrubTime / duration : currentTime / duration) * 100;

  /**
   * Document-level mouse event listeners for smooth dragging
   * 
   * **Why document-level?**
   * Attaching to document instead of the playhead element prevents losing
   * the drag when the mouse moves quickly or leaves the playhead bounds.
   * This provides a much smoother drag experience.
   * 
   * Listeners are only active during drag and cleaned up on mouseUp or
   * when component unmounts.
   */
  useEffect(() => {
    if (!dragging) return;

    const handleDocumentMouseMove = (e) => handleMouseMove(e);
    const handleDocumentMouseUp = () => handleMouseUp();

    document.addEventListener('mousemove', handleDocumentMouseMove);
    document.addEventListener('mouseup', handleDocumentMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, [dragging, refBounds, duration]);

  return (
    <>
      <div
        style={{ ...handleStyle, left: `${handlePosition}%` }}
        onMouseDown={handleMouseDown}
        onClick={handleClickNav}
        id="playback-handle"
      />
      <div style={{ ...handleLineStyle, left: `${handlePosition}%` }} id="playback-handle-line"></div>
    </>
  );
});

export default Playhead;
